// Compile/apply actions for playground editor.
// Example: runApply() writes compiled CSS into preview style tag.
import { compileLuis, type LuisCompileResult } from '@lentystyle/compiler'
import { applyLuisToDocument } from '@lentystyle/runtime'
import { elements, PLAYGROUND_STYLE_ID } from './state'
import { formatDuration } from './format'
import {
  handleError,
  logCompileWarnings,
  renderResult,
  setCompilePerf,
  setStatus,
} from './render'
import { playgroundMessages } from './messages'

// Compile current source and refresh output panes.
export function runCompile(showStatus = true, sourceOverride?: string): LuisCompileResult {
  try {
    const source = sourceOverride ?? elements.luisInput.value
    const { result, durationMs } = compileWithTiming(source)
    logCompileWarnings(result.warnings)
    renderResult(result)
    setCompilePerf(durationMs)
    if (showStatus) {
      setStatus(formatStatus(playgroundMessages.compilationSucceeded, formatDuration(durationMs), result.warnings.length), 'ok')
    }
    return result
  } catch (error) {
    handleError(error)
    throw error
  }
}

// Apply compile result CSS to the live document preview style tag.
export function runApply(): void {
  runApplyInternal()
}

// Compile source and write style output into preview style tag.
export function runApplyInternal(sourceOverride?: string): LuisCompileResult {
  try {
    const source = sourceOverride ?? elements.luisInput.value
    const { result, durationMs } = applyWithTiming(source)
    logCompileWarnings(result.warnings)
    renderResult(result)
    setCompilePerf(durationMs)
    setStatus(formatStatus(playgroundMessages.stylesApplied, formatDuration(durationMs), result.warnings.length), 'ok')
    return result
  } catch (error) {
    handleError(error)
    throw error
  }
}

// Measure compile duration for performance display.
function compileWithTiming(source: string): { result: LuisCompileResult; durationMs: number } {
  const start = performance.now()
  const result = compileLuis(source)
  const durationMs = performance.now() - start
  return { result, durationMs }
}

// Measure guarded compile/apply duration for the preview document.
function applyWithTiming(source: string): { result: LuisCompileResult; durationMs: number } {
  const start = performance.now()
  const result = applyLuisToDocument(source, PLAYGROUND_STYLE_ID)
  const durationMs = performance.now() - start
  return { result, durationMs }
}

function formatStatus(prefix: string, duration: string, warningCount: number): string {
  const warningSuffix = warningCount > 0 ? `, ${playgroundMessages.warningsLabel}: ${warningCount}` : ''
  return `${prefix} (${duration}${warningSuffix}).`
}
